import { endOfMonth, format, startOfMonth } from 'date-fns';
import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import { OVERCOST, PROVIDERS } from 'constants/FilterConstants';
import { getLocaleUsage } from 'constants/LanguageConstants';
import { PATH } from 'constants/RoutesConstants';
import { API_ASCENDING } from 'constants/SortConstants';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { getLocale } from 'modules/i18n/selectors/data';
import type { MetricsReducer } from 'modules/metrics/reducers';
import type { BillingStatisticExportRequest, BillingMetricsQuery } from 'modules/metrics/types/actions';
import { EXPORT_BILLING_TYPE, type WrappedBilling } from 'modules/metrics/types/billing';
import { getLocation } from 'modules/router/selectors';
import { getCurrentUserIsAdmin } from 'modules/users/selectors/currentUser';
import { getSelectedDealerGroupId, getSelectedDealerId } from 'selectors/dealerSelectors';
import { tryParse } from 'utils/date';
import { cleanPayload } from 'utils/RequestUtils';

import {
  BILLING_PERIOD,
  BILLING_PERIOD_FORMAT,
  BILLING_SCOPE,
  BILLING_TYPE,
  DOCUMENTS_EXPORT_ENTITY,
  type BillingScopeEnum,
  type BillingTypeEnum,
} from '../constants';

type State = { metrics: MetricsReducer };

/** Which endpoint the current scope reads: the group aggregation, or the dealers of one group. */
type BillingFetchParams = BillingMetricsQuery & { url: BillingScopeEnum };

const getState = (state: State) => state.metrics;

const isBillingType = (value?: string): value is BillingTypeEnum =>
  Object.values<string>(BILLING_TYPE).includes(value ?? '');

/** The name a row is sorted by: dealer rows carry one field, group rows the other. */
const getRowName = (row: WrappedBilling) => ('dealerName' in row ? row.dealerName : row.groupName);

export const isExporting = createSelector(getState, state => state.exporting);

export const getEntity = createSelector(getLocation, location => {
  const match = matchPath(PATH.BILLING, location.pathname);
  const entity = match?.params?.entity?.toUpperCase();

  return isBillingType(entity) ? entity : BILLING_TYPE.MESSAGE;
});

export const getSelectedDocumentsTab = createSelector(getState, state => state.selectedDocumentsTab);

export const getEffectiveEntity = createSelector(
  getEntity,
  getSelectedDocumentsTab,
  (entity, tab) => (entity === BILLING_TYPE.DOCUMENTS ? tab : entity)
);

const getEntitySlice = createSelector(getState, getEffectiveEntity, (state, entity) => state[entity]);

const getFilterEntitySlice = createSelector(getState, getEntity, (state, entity) => state[entity]);

export const getFilters = createSelector(getFilterEntitySlice, slice => slice.filters);

export const getExportEntity = createSelector(getEntity, entity =>
  (entity === BILLING_TYPE.DOCUMENTS ? DOCUMENTS_EXPORT_ENTITY : entity));

export const getSort = createSelector(getFilters, filters => filters.sort);

export const getFilterPeriod = createSelector(getFilters, ({ from, to }) => ({
  periodType: BILLING_PERIOD.MONTHLY,
  from,
  to,
}));

export const isLoading = createSelector(getEntitySlice, state => state.fetching);

export const getApiError = createSelector(getEntitySlice, state => state.apiError);

const getDatasource = createSelector(getEntitySlice, state => state.data);

const sorterCollection: Record<string, (datasource: WrappedBilling[], locale: string) => WrappedBilling[]> = {
  name: (datasource, locale) => {
    const collator = new Intl.Collator(getLocaleUsage(locale).i18n);
    return datasource.toSorted((a, b) => collator.compare(getRowName(a), getRowName(b)));
  },
  count: datasource => datasource.toSorted((a, b) => a.count - b.count),
  duration: datasource => datasource.toSorted((a, b) => (a.duration ?? 0) - (b.duration ?? 0)),
};

const getSortedDatasource = createSelector(getSort, getLocale, getDatasource, (sort, locale, datasource) => {
  const [dataKey, direction] = sort.split(',');
  const sorted = sorterCollection[dataKey]?.(datasource, locale) ?? datasource;
  return direction === API_ASCENDING ? sorted : sorted.toReversed();
});

export const getFilteredDatasource = createSelector(
  getSortedDatasource,
  getFilters,
  (datasource, { [PROVIDERS]: providers, [OVERCOST]: overcost }) => {
    const hasProvider = Boolean(providers?.length);

    return datasource.reduce<WrappedBilling[]>((acc, item) => {
      const provider = 'provider' in item ? item.provider : undefined;
      const chargedOvercost = 'chargedOvercost' in item ? item.chargedOvercost : undefined;

      const hasRightProvider =
        !hasProvider || Boolean(providers?.some(value => value.toUpperCase() === provider?.toUpperCase()));

      const matchOvercostFilter = !overcost || (chargedOvercost ?? 0) > 0;

      if (hasRightProvider && matchOvercostFilter) {
        acc.push(item);
      }
      return acc;
    }, []);
  }
);

export const allowDrawerFilter = createSelector(getSelectedGroupId, groupId => groupId);

export const getFetchParams = createSelector(
  getFilterPeriod,
  getSelectedDealerGroupId,
  getSelectedDealerId,
  getCurrentUserIsAdmin,
  ({ to, from }, groupId, dealerId, isAdmin): BillingFetchParams => {
    const parsedFrom = tryParse(from) ?? startOfMonth(new Date());
    const parsedTo = tryParse(to) ?? endOfMonth(new Date());

    return {
      to: format(parsedTo, BILLING_PERIOD_FORMAT),
      from: format(parsedFrom, BILLING_PERIOD_FORMAT),
      periodType: BILLING_PERIOD.MONTHLY,
      ...(isAdmin && !groupId
        ? { url: BILLING_SCOPE.GROUPS }
        : { url: BILLING_SCOPE.DEALERS, groupId, ...(dealerId ? { dealerId } : {}) }),
    };
  }
);

export const getExportParams = createSelector(
  getFilters,
  getFetchParams,
  getCurrentUserIsAdmin,
  ({ from: _, to: __, sort: sortStr, ...filters }, { url: ___, ...fetchParams }, isAdmin): Omit<BillingStatisticExportRequest, 'email'> => {
    const [sort, order] = sortStr.split(',');

    return {
      ...fetchParams,
      exportInfos: {
        type: isAdmin ? EXPORT_BILLING_TYPE.ADMIN : EXPORT_BILLING_TYPE.MANAGER,
        filters: Object.entries(cleanPayload(filters)).reduce((acc, [field, value]) => {
          if (!Array.isArray(value) || value.length > 0) {
            return { ...acc, [field]: value };
          }
          return acc;
        }, {}),
        sort,
        order: order.toUpperCase(),
      },
    };
  }
);
